import React from 'react';
import { ClockIcon, PhoneIcon, MailIcon, ActivityIcon } from 'lucide-react';
import { BorrowerMetrics } from '../data/mockData';
interface SummaryBarProps {
  metrics: BorrowerMetrics;
}
export function SummaryBar({
  metrics
}: SummaryBarProps) {
  // Format duration from seconds to "Xm Ys"
  const formatDuration = (seconds: number | null) => {
    if (seconds === null) return '--';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  };
  // Format date to relative or short string
  const formatRelativeDate = (isoString: string | null) => {
    if (!isoString) return 'Never';
    const date = new Date(isoString);
    const now = new Date();
    const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    if (diffHours < 24) {
      return diffHours === 0 ? 'Just now' : `${diffHours}h ago`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
  };
  // Determine engagement status
  const getEngagementStatus = (isoString: string | null) => {
    if (!isoString) return {
      text: 'No Contact',
      color: 'text-gray-500',
      dot: 'bg-gray-400'
    };
    const date = new Date(isoString);
    const diffDays = Math.floor((new Date().getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 3) return {
      text: 'Active',
      color: 'text-emerald-600',
      dot: 'bg-emerald-500'
    };
    if (diffDays <= 7) return {
      text: 'Cooling Off',
      color: 'text-amber-600',
      dot: 'bg-amber-500'
    };
    return {
      text: 'Stale',
      color: 'text-red-600',
      dot: 'bg-red-500'
    };
  };
  const engagement = getEngagementStatus(metrics.lastMeaningfulContact);
  const getDispositionColor = (disposition: string | null) => {
    switch (disposition) {
      case 'Connected':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'Voicemail':
      case 'Left Message':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'No Answer':
      case 'Busy':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  const emailEngagementRate = metrics.emailsSent > 0 ? Math.round(metrics.emailsOpened / metrics.emailsSent * 100) : 0;
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-id="element-140">
      {/* Last Meaningful Contact */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col items-center text-center min-h-[140px]" data-id="element-141">
        <div className="flex items-center justify-center text-gray-500 mb-3" data-id="element-142">
          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center mr-3" data-id="element-143">
            <ActivityIcon className="w-4 h-4 text-blue-600" data-id="element-144" />
          </div>
          <span className="text-xs font-semibold uppercase tracking-wider" data-id="element-145">
            Last Meaningful Contact
          </span>
        </div>
        <div className="mt-auto" data-id="element-146">
          <div className="text-xl font-bold text-gray-900" data-id="element-147">
            {formatRelativeDate(metrics.lastMeaningfulContact)}
          </div>
          <div className={`text-sm font-medium mt-1 flex items-center justify-center ${engagement.color}`} data-id="element-148">
            <span className={`w-2 h-2 rounded-full mr-2 ${engagement.dot}`} data-id="element-149"></span>
            {engagement.text}
          </div>
        </div>
      </div>

      {/* Last Call Outcome */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col items-center text-center min-h-[140px]" data-id="element-150">
        <div className="flex items-center justify-center text-gray-500 mb-3" data-id="element-151">
          <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center mr-3" data-id="element-152">
            <PhoneIcon className="w-4 h-4 text-emerald-600" data-id="element-153" />
          </div>
          <span className="text-xs font-semibold uppercase tracking-wider" data-id="element-154">
            Last Call Outcome
          </span>
        </div>
        <div className="mt-auto" data-id="element-155">
          <div className="mb-1" data-id="element-156">
            {metrics.lastCallDisposition ? <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border ${getDispositionColor(metrics.lastCallDisposition)}`} data-id="element-157">
                {metrics.lastCallDisposition}
              </span> : <span className="text-gray-500 text-sm" data-id="element-158">No calls recorded</span>}
          </div>
          <div className="text-sm text-gray-500 mt-1" data-id="element-159">
            {metrics.lastMeaningfulContact ? new Date(metrics.lastMeaningfulContact).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
          }) : 'No data'}
          </div>
        </div>
      </div>

      {/* Call Duration */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col items-center text-center min-h-[140px]" data-id="element-160">
        <div className="flex items-center justify-center text-gray-500 mb-3" data-id="element-161">
          <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center mr-3" data-id="element-162">
            <ClockIcon className="w-4 h-4 text-amber-600" data-id="element-163" />
          </div>
          <span className="text-xs font-semibold uppercase tracking-wider" data-id="element-164">
            Last Call Duration
          </span>
        </div>
        <div className="mt-auto" data-id="element-165">
          <div className="text-2xl font-bold text-gray-900" data-id="element-166">
            {formatDuration(metrics.lastCallDuration)}
          </div>
          <div className="text-sm text-gray-500 mt-1" data-id="element-167">
            {metrics.lastCallDuration && metrics.lastCallDuration > 300 ? 'Substantive conversation' : 'Brief interaction'}
          </div>
        </div>
      </div>

      {/* Email Engagement */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col items-center text-center min-h-[140px]" data-id="element-168">
        <div className="flex items-center justify-center text-gray-500 mb-3" data-id="element-169">
          <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center mr-3" data-id="element-170">
            <MailIcon className="w-4 h-4 text-indigo-600" data-id="element-171" />
          </div>
          <span className="text-xs font-semibold uppercase tracking-wider" data-id="element-172">
            Email Engagement
          </span>
        </div>
        <div className="mt-auto w-full" data-id="element-173">
          <div className="flex items-baseline justify-center mb-2" data-id="element-174">
            <span className="text-2xl font-bold text-gray-900" data-id="element-175">
              {emailEngagementRate}%
            </span>
            <span className="text-sm text-gray-500 ml-2" data-id="element-176">
              ({metrics.emailsOpened} of {metrics.emailsSent} opened)
            </span>
          </div>
          {/* Progress Bar */}
          <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden" data-id="element-177">
            <div className="bg-indigo-500 h-1.5 rounded-full transition-all duration-500" style={{
            width: `${emailEngagementRate}%`
          }} data-id="element-178"></div>
          </div>
        </div>
      </div>
    </div>;
}